// src/views/components/LeadPopup/LeadPopup.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, CheckCircle2, MessageCircle, Sparkles, Lock, ShieldCheck, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLeadContext } from '../../../controllers/context/LeadContext';
import { useSiteSettings } from '../../../controllers/context/SiteSettingsContext';
import { saveSolutionLead, generateWhatsAppLink } from '../../../controllers/services/leadService';
import Button from '../Button/Button';
import '../components.css';
import logoImg from '../../../assets/logo.png';

const REQUIREMENTS = [
  'Buy a Property',
  'Sell a Property',
  'Rent a Property',
  'New House Construction',
  'Commercial Construction',
  'Renovation & Repair',
  'Architecture & Building Plan'
];

const CITIES = ['Ghazipur', 'Varanasi', 'Lucknow', 'Other'];

const PROPERTY_TYPES = ['Residential', 'Commercial', 'Plot/Land'];

const getBudgetConfig = (requirement) => {
  switch (requirement) {
    case 'Buy a Property':
      return {
        label: 'Budget Range',
        options: ['Under 10 Lakh', '10 - 25 Lakh', '25 - 50 Lakh', '50 Lakh - 1 Cr', '1 Cr+']
      };
    case 'Rent a Property':
      return {
        label: 'Monthly Budget',
        options: ['Under 5k', '5k - 10k', '10k - 20k', '20k - 50k', '50k+']
      };
    case 'Sell a Property':
      return {
        label: 'Expected Price',
        options: ['Under 25 Lakh', '25 - 50 Lakh', '50 Lakh - 1 Cr', '1 - 2 Cr', '2 Cr+']
      };
    case 'New House Construction':
    case 'Commercial Construction':
      return {
        label: 'Estimated Project Budget',
        options: ['Under 15 Lakh', '15 - 30 Lakh', '30 - 60 Lakh', '60 Lakh - 1 Cr', '1 Cr+']
      };
    case 'Renovation & Repair':
    case 'Architecture & Building Plan':
      return {
        label: 'Estimated Budget',
        options: ['Under 50,000', '50,000 - 2 Lakh', '2 - 5 Lakh', '5 - 10 Lakh', '10 Lakh+']
      };
    default:
      return {
        label: 'Budget Range',
        options: ['Under 10 Lakh', '10 - 25 Lakh', '25 - 50 Lakh', '50 Lakh - 1 Cr', '1 Cr+']
      };
  }
};

const LeadPopup = () => {
  const { isPopupOpen, popupData, closePopup } = useLeadContext();
  const { whatsappPhone } = useSiteSettings();

  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    requirement: 'Buy a Property',
    city: 'Ghazipur',
    property_type: 'Residential',
    budget: '',
    details: '',
  });

  const [honeypot, setHoneypot] = useState('');
  const [consent, setConsent] = useState(true);
  const [phoneError, setPhoneError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const mountTimeRef = useRef(Date.now());
  const firstInputRef = useRef(null);

  // Close with session mark so it doesn't auto-reopen in the same session
  const handleDismiss = useCallback(() => {
    sessionStorage.setItem('enquiryPopupDismissed', 'true');
    closePopup();
  }, [closePopup]);

  // Keyboard accessibility: Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isPopupOpen) {
        handleDismiss();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isPopupOpen, handleDismiss]);

  // Adapt pre-selected requirement based on popupData
  useEffect(() => {
    if (isPopupOpen) {
      mountTimeRef.current = Date.now();
      setSubmitted(false);
      setPhoneError('');
      setSubmitError('');
      setHoneypot('');

      let initialRequirement = 'Buy a Property';
      if (popupData?.requirement && REQUIREMENTS.includes(popupData.requirement)) {
        initialRequirement = popupData.requirement;
      } else if (popupData?.type === 'construction' || popupData?.source?.includes('construction')) {
        initialRequirement = 'New House Construction';
      } else if (popupData?.type === 'builder' || popupData?.source?.includes('builder')) {
        initialRequirement = 'Commercial Construction';
      } else if (popupData?.type === 'renter') {
        initialRequirement = 'Rent a Property';
      } else if (popupData?.type === 'seller') {
        initialRequirement = 'Sell a Property';
      }

      setFormData({
        name: '',
        mobile: '',
        requirement: initialRequirement,
        city: popupData?.city || 'Ghazipur',
        property_type: 'Residential',
        budget: '',
        details: popupData?.details || '',
      });

      // Auto-focus first input
      setTimeout(() => {
        if (firstInputRef.current) {
          firstInputRef.current.focus();
        }
      }, 150);
    }
  }, [isPopupOpen, popupData]);

  const budgetConfig = getBudgetConfig(formData.requirement);

  // Smart Label calculation (Rule 7)
  const isConstructionOrArchitecture =
    formData.requirement.includes('Construction') ||
    formData.requirement.includes('Renovation') ||
    formData.requirement.includes('Architecture');
  const propertyTypeLabel = isConstructionOrArchitecture ? 'Project Type' : 'Property Type';

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'mobile') {
      // Keep only numbers and max 10 digits
      const cleaned = value.replace(/\D/g, '').slice(0, 10);
      setFormData((prev) => ({ ...prev, mobile: cleaned }));
      if (cleaned.length === 10 && !/^[6-9]\d{9}$/.test(cleaned)) {
        setPhoneError('Please enter a valid 10-digit mobile number starting with 6-9');
      } else {
        setPhoneError('');
      }
      return;
    }

    if (name === 'requirement') {
      // Reset budget when requirement type changes
      setFormData((prev) => ({
        ...prev,
        requirement: value,
        budget: '',
      }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');

    // Honeypot bot protection
    if (honeypot && honeypot.trim() !== '') {
      setSubmitted(true);
      return;
    }

    // Speed trap protection (bots fill forms in <1.2s)
    if (Date.now() - mountTimeRef.current < 1200) {
      setSubmitted(true);
      return;
    }

    // Phone validation
    const cleanMobile = formData.mobile.replace(/\D/g, '');
    if (!/^[6-9]\d{9}$/.test(cleanMobile)) {
      setPhoneError('Please enter a valid 10-digit Indian mobile number starting with 6-9');
      return;
    }

    if (!formData.name || formData.name.trim().length < 2) {
      setSubmitError('Please enter your full name');
      return;
    }

    if (!consent) {
      setSubmitError('Main sahmat hoon ki meri details share ki jayein par tick lagana anivarya hai.');
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        name: formData.name.trim(),
        mobile: cleanMobile,
        requirement: formData.requirement,
        city: formData.city,
        property_type: formData.property_type || null,
        budget: formData.budget || null,
        details: formData.details ? formData.details.trim() : null,
        honeypot: honeypot,
        source: popupData?.source || 'website_modal',
      };

      const result = await saveSolutionLead(payload);

      if (result.success) {
        setSubmitted(true);
        sessionStorage.setItem('enquiryPopupDismissed', 'true');
      } else {
        setSubmitError(result.error || 'Failed to submit enquiry. Please try again.');
      }
    } catch (err) {
      console.error('Lead submit error:', err);
      setSubmitError('An unexpected error occurred. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleWhatsAppRedirect = () => {
    const msg = `Hi NOBROKERAGE, my name is ${formData.name}. I am looking for ${formData.requirement} in ${formData.city} (Budget: ${formData.budget || 'Flexible'}).`;
    window.open(generateWhatsAppLink(msg, whatsappPhone), '_blank');
    handleDismiss();
  };

  return (
    <AnimatePresence>
      {isPopupOpen && (
        <motion.div
          className="popup-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={(e) => {
            if (e.target === e.currentTarget) handleDismiss();
          }}
        >
          <motion.div
            className="popup-content solution-popup-content"
            initial={{ scale: 0.94, y: 20, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.94, y: 20, opacity: 0 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="enquiry-modal-title"
          >
            <button
              className="popup-close"
              onClick={handleDismiss}
              aria-label="Close modal"
            >
              <X size={18} />
            </button>

            {/* Header: Clean #FFFEFE Background without pink gradient */}
            <div className="popup-header solution-popup-header">
              <div className="solution-logo-row">
                <img
                  src={logoImg}
                  alt="No Brokerage"
                  className="solution-logo-img"
                  width="120"
                  height="40"
                />
              </div>

              <div className="solution-badge-row">
                <span className="solution-badge">
                  <Sparkles size={12} style={{ marginRight: '5px' }} />
                  Zero Brokerage Assistance
                </span>
              </div>

              <h2 id="enquiry-modal-title" className="solution-popup-title">
                Find Your Property Solution
              </h2>

              <div className="solution-pills-row">
                <span>Buy</span> • <span>Sell</span> • <span>Rent</span> • <span>Build</span>
              </div>

              {!submitted && (
                <p className="solution-popup-subtitle">
                  Share your requirements, and our team will connect you with verified local advisors.
                </p>
              )}
            </div>

            {/* Body */}
            <div className="popup-body solution-popup-body">
              {submitted ? (
                <motion.div
                  className="solution-success-screen"
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.25 }}
                >
                  <div className="solution-success-icon-wrap">
                    <CheckCircle2 size={54} color="var(--success)" />
                  </div>
                  <h3 className="solution-success-title">Thank You!</h3>
                  <p className="solution-success-msg">
                    Thank you, hamari team jaldi aapse sampark karegi.
                  </p>
                  <p className="solution-success-detail">
                    We have received your requirement for <strong>{formData.requirement}</strong> in <strong>{formData.city}</strong>. Our property expert will call you on <strong>+91 {formData.mobile}</strong> shortly.
                  </p>

                  <div className="solution-success-actions">
                    <Button
                      variant="primary"
                      fullWidth
                      onClick={handleWhatsAppRedirect}
                      className="solution-whatsapp-btn"
                    >
                      <MessageCircle size={18} />
                      Chat on WhatsApp Now
                    </Button>
                    <button
                      type="button"
                      className="solution-dismiss-btn"
                      onClick={handleDismiss}
                    >
                      Close Window
                    </button>
                  </div>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} noValidate>
                  {/* Honeypot hidden input */}
                  <input
                    type="text"
                    name="company_website_hp"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                    style={{ display: 'none', position: 'absolute', opacity: 0, zIndex: -1 }}
                    tabIndex={-1}
                    autoComplete="off"
                  />

                  {submitError && (
                    <div className="solution-error-banner" role="alert">
                      {submitError}
                    </div>
                  )}

                  {/* Row 1: Name and Mobile */}
                  <div className="solution-form-grid">
                    <div className="input-group">
                      <label className="input-label" htmlFor="lead_name">
                        Full Name <span style={{ color: 'var(--brand-red)' }}>*</span>
                      </label>
                      <input
                        ref={firstInputRef}
                        id="lead_name"
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="e.g. Ramesh Kumar"
                        className="input-field"
                        required
                      />
                    </div>

                    <div className="input-group">
                      <label className="input-label" htmlFor="lead_mobile">
                        Mobile Number <span style={{ color: 'var(--brand-red)' }}>*</span>
                      </label>
                      <div className={`solution-phone-wrap ${phoneError ? 'has-error' : ''}`}>
                        <div className="solution-phone-prefix-box">+91</div>
                        <input
                          id="lead_mobile"
                          type="tel"
                          name="mobile"
                          value={formData.mobile}
                          onChange={handleChange}
                          placeholder="10-digit number"
                          maxLength={10}
                          inputMode="numeric"
                          className="solution-phone-input-raw"
                          required
                        />
                      </div>
                      {phoneError && (
                        <span className="solution-field-error" role="alert">{phoneError}</span>
                      )}
                    </div>
                  </div>

                  {/* Row 2: Your Requirement */}
                  <div className="input-group">
                    <label className="input-label" htmlFor="lead_requirement">
                      Your Requirement <span style={{ color: 'var(--brand-red)' }}>*</span>
                    </label>
                    <select
                      id="lead_requirement"
                      name="requirement"
                      value={formData.requirement}
                      onChange={handleChange}
                      className="input-field"
                      required
                    >
                      {REQUIREMENTS.map((req) => (
                        <option key={req} value={req}>
                          {req}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Row 3: City and Property Type / Project Type (Smart Label) */}
                  <div className="solution-form-grid">
                    <div className="input-group">
                      <label className="input-label" htmlFor="lead_city">
                        Select City <span style={{ color: 'var(--brand-red)' }}>*</span>
                      </label>
                      <select
                        id="lead_city"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        className="input-field"
                        required
                      >
                        {CITIES.map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="input-group">
                      <label className="input-label" htmlFor="lead_property_type">
                        {propertyTypeLabel}
                      </label>
                      <select
                        id="lead_property_type"
                        name="property_type"
                        value={formData.property_type}
                        onChange={handleChange}
                        className="input-field"
                      >
                        <option value="">Select Type (Optional)</option>
                        {PROPERTY_TYPES.map((pt) => (
                          <option key={pt} value={pt}>
                            {pt}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Row 4: Dynamic Budget / Expected Price (Smart Label) */}
                  <div className="input-group">
                    <label className="input-label" htmlFor="lead_budget">
                      {budgetConfig.label}
                    </label>
                    <select
                      id="lead_budget"
                      name="budget"
                      value={formData.budget}
                      onChange={handleChange}
                      className="input-field"
                    >
                      <option value="">Select Range (Optional)</option>
                      {budgetConfig.options.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Row 5: Additional Details */}
                  <div className="input-group">
                    <label className="input-label" htmlFor="lead_details">
                      Additional Details (Optional)
                    </label>
                    <textarea
                      id="lead_details"
                      name="details"
                      rows={2}
                      value={formData.details}
                      onChange={handleChange}
                      placeholder="e.g. Preferred locality, plot size, or specific requirements..."
                      className="input-field solution-textarea"
                    />
                  </div>

                  {/* Consent Checkbox (Rule 9) */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '10px',
                    margin: '6px 0 14px 0',
                    padding: '8px 10px',
                    background: 'rgba(0,0,0,0.02)',
                    borderRadius: '8px',
                    border: '1px solid rgba(0,0,0,0.06)'
                  }}>
                    <input
                      type="checkbox"
                      id="lead_consent"
                      checked={consent}
                      onChange={(e) => setConsent(e.target.checked)}
                      style={{ marginTop: '3px', cursor: 'pointer', accentColor: 'var(--brand-red, #A8080C)' }}
                    />
                    <label
                      htmlFor="lead_consent"
                      style={{
                        fontSize: '0.78rem',
                        lineHeight: 1.4,
                        color: 'var(--text-secondary, #555)',
                        cursor: 'pointer',
                        userSelect: 'none'
                      }}
                    >
                      Main sahmat hoon ki meri details property owner / No Brokerage team ke saath share ki jayein.
                    </label>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={submitting || !consent}
                    className="solution-submit-btn"
                  >
                    {submitting ? (
                      <>
                        <span
                          style={{
                            width: 16,
                            height: 16,
                            border: '2px solid rgba(255,255,255,0.3)',
                            borderTopColor: '#ffffff',
                            borderRadius: '50%',
                            display: 'inline-block',
                            animation: 'spin 0.8s linear infinite'
                          }}
                        />
                        <span>Submitting...</span>
                      </>
                    ) : (
                      'Get Free Consultation'
                    )}
                  </button>

                  {/* Trust Line with SVG Icons (Rule 9) */}
                  <div className="solution-guarantee-note">
                    <span className="solution-guarantee-item">
                      <Lock size={12} color="var(--brand-red)" /> 100% Free Consultation
                    </span>
                    <span>•</span>
                    <span className="solution-guarantee-item">
                      <ShieldCheck size={13} color="var(--brand-red)" /> Verified Local Advisors
                    </span>
                    <span>•</span>
                    <span className="solution-guarantee-item">
                      <Check size={13} color="var(--brand-red)" /> No Spam Calls
                    </span>
                  </div>
                </form>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LeadPopup;
